import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { VoteClass, VoteData, VotePrivateData } from 'whocan-lib';
import { PathService } from '../../core/path.service';
import { AuthService } from '../../../user/auth.service';

import { CurrentPollService } from '../../core/current-poll.service';
import { isNetworkRequestFailed } from '../../core/poll-errors';
import { VotesService } from './votes.service';
import { OptionsService } from '../../shared/options/options.service';
import { HeaderService } from '../../shared/poll-header/header.service';
import { PollParticipantsService } from '../../shared/poll-header/poll-participants.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import isEqual from 'lodash/isEqual';

/**
 * The current voter's vote form. Three things, kept apart (concept-own-form-state-2026-09-18):
 * the vote LIST (VotesService.voteClassList) is the database projection every display and count reads;
 * the FORM STATE (userVote) is a vote object of its own - a copy of the own list entry or an empty vote -
 * that never lies in the list; the BASELINE (_userVoteSinceLastSave) is a snapshot of the form after
 * loading and saving, follows the server on changes made elsewhere and tells whether the form is dirty.
 */
@Injectable({
    providedIn: 'root'
})
export class UserVoteService {
    userVote: VoteClass;
    public isLoggingOut: boolean = false
    public isNewVote: boolean = false; /** true while the current voter has no persisted vote (no list entry with ownUserId) */
    private _userVoteSinceLastSave: VoteClass; /** baseline of the form: as loaded or last saved, see hasUnsavedChanges */
    private _isSaving: boolean = false; /** saveVote is waiting for its own write, see onVotesUpdated */
    private _votesUpdatedSubscription: Subscription | null = null;
    private _optionsUpdatedSubscription: Subscription | null = null;
    constructor(
        private logger: LoggerService,
        private afd: AngularFireDatabase,
        private pathService: PathService,
        private votesService: VotesService,
        private authService: AuthService,
        private currentPollService: CurrentPollService,
        private optionsService: OptionsService,
        private headerService: HeaderService,
        private pollParticipantsService: PollParticipantsService,
        private googleAnayltics: GoogleAnalyticsService,
    ) {
        this.initUserVote()
    }

    public async initUserVote(voteUserId?: string): Promise<void> {
        this.logger.log('initUserVote')
        this.uninitialize()
        this.userVote = this.getUserVote(voteUserId)
        await this.loadUserVotePrivateData()
        this.resetVoteSinceLastSave()
        this.isLoggingOut = false;
        this.subscribeToUpdates()
    }

    public uninitialize(): void {
        this._votesUpdatedSubscription?.unsubscribe();
        this._votesUpdatedSubscription = null;
        this._optionsUpdatedSubscription?.unsubscribe();
        this._optionsUpdatedSubscription = null;
    }

    /**
     * Subscribed in initUserVote, i.e. before PollService.loadPollData subscribes the realtime updates and
     * before any component - Subjects emit in subscription order, so the form is up to date when the
     * components rebuild. Unsubscribes first: a second initUserVote may have run during the await.
     */
    private subscribeToUpdates(): void {
        this.uninitialize();
        this._votesUpdatedSubscription = this.votesService.votesUpdated$.subscribe(() => this.onVotesUpdated());
        this._optionsUpdatedSubscription = this.optionsService.optionsUpdated$.subscribe(() => this.padChoiceLists());
    }

    /**
     * Loads the contact part (votePrivate node) of the user's own persisted vote and merges
     * it in, so the vote form prefills email/phone/private comment. The rules allow the
     * self read; a missing node is no error (votes from before the backfill). Must run
     * before resetVoteSinceLastSave — otherwise the merge would count as an unsaved change.
     */
    private async loadUserVotePrivateData(): Promise<void> {
        if (this.isNewVote || !this.userVote?.userId) return;
        try {
            const privateData = await firstValueFrom(
                this.afd.object<VotePrivateData>(
                    this.pathService.getUserVotePrivatePath(this.userVote.userId)
                ).valueChanges()
            );
            this.userVote.applyPrivateData(privateData);
        } catch (error) {
            // e.g. no read grant (foreign voteUserId in the URL) - the form just starts without prefill
            this.logger.warn('loadUserVotePrivateData failed', error);
        }
    }

    private resetVoteSinceLastSave() {
        this._userVoteSinceLastSave = this.userVote.cloneDeep()
    }

    /** THE identity of "own" everywhere: the voter whose vote the form edits - the delegated voter via an access link, otherwise the signed-in user */
    get ownUserId(): string | undefined {
        return this.userVote?.userId;
    }

    /** the own vote as the database has it: the list entry with ownUserId, undefined while there is none */
    get ownPersistedVote(): VoteClass | undefined {
        const ownUserId = this.ownUserId;
        return ownUserId ? this.votesService.voteClassList.find(vote => vote.userId === ownUserId) : undefined;
    }

    private get numberOfOptions(): number {
        return this.optionsService.optionList.lengthIncludingDeleted;
    }

    /**
     * Persons the current voter's PERSISTED vote registers for the header people limit
     * (VoteClass.registeredPersons of the baseline, 0 for a new vote). The form changes while
     * the voter types, so the limit maths must not read it - otherwise the "spots left" hint
     * would jump with every keystroke (workorder-header-max-persons-2026-09-10, task 4).
     */
    get persistedRegisteredPersons(): number {
        if (this.isNewVote) return 0;
        return this._userVoteSinceLastSave?.registeredPersons ?? 0;
    }

    /** a COPY of the persisted vote, or a fresh one - never a reference into the list, never pushed into it */
    private getUserVote(voteUserId?: string): VoteClass {
        const uid = voteUserId || this.authService.uid;
        const persisted = this.votesService.voteClassList.find(vote => vote.userId === uid);
        if (persisted) {
            this.logger.log('getUserVote: user vote exists', persisted)
            this.isNewVote = false;
            return persisted.cloneDeep();
        }
        this.logger.log('getUserVote: no user vote')
        this.isNewVote = true;
        return new VoteClass(this.newVoteData(), this.numberOfOptions);
    }

    private newVoteData(): VoteData {
        return {
            userId: this.authService.uid,
            userName: this.authService.displayName,
            userEmail: this.authService.email,
            choicesArray: []
        };
    }

    /**
     * The replacement for the keep-reference special case that used to live in VotesService:
     * the list is rebuilt from the server without exceptions, and the form decides what to do
     * with the own entry (concept-own-form-state-2026-09-18 §3.3, E4a/E5a) - take the server
     * state over while the form is clean, keep the draft and move the baseline while it is dirty.
     */
    private onVotesUpdated(): void {
        if (!this.userVote) return;
        const entry = this.ownPersistedVote;
        if (this._isSaving) {
            // the echo of the own write - or its revert when the server rejects the write: the form holds
            // exactly what is being written, so it only takes the server-owned timestamps; saveVote sets
            // the baseline once the write is confirmed. A revert must neither look like "deleted elsewhere"
            // nor like a server state to take over - both would wipe the voter's input.
            this.adoptTimestamps(this.userVote, entry);
            return;
        }
        if (!entry) {
            if (!this.isNewVote) { // deleted elsewhere: own row in the dashboard, second tab
                this.isNewVote = true;
                this.userVote.replaceData(this.newVoteData(), this.numberOfOptions);
                this.resetVoteSinceLastSave();
            }
            return;
        }
        this.isNewVote = false;
        if (this.hasUnsavedChanges) {
            this.adoptServerState(this._userVoteSinceLastSave, entry); // the draft stays, the baseline follows the server
        } else {
            this.adoptServerState(this.userVote, entry);
            this.resetVoteSinceLastSave();
        }
    }

    /**
     * Server state into target, in place. The choice list is only rebuilt when the persisted public
     * part really differs - every foreign vote triggers this, and rebuilding would swap the choice
     * objects under the open form for nothing. Contact fields are kept unless the entry carries them
     * (owner path, VotesService.applyPrivateDataToVoteList); the timestamps always come along.
     */
    private adoptServerState(target: VoteClass, entry: VoteClass): void {
        if (isEqual(this.comparable(target, 'public'), this.comparable(entry, 'public'))) {
            this.adoptTimestamps(target, entry);
        } else {
            const keep = target.dataPrivate;
            target.replaceData(entry.data, this.numberOfOptions);
            target.applyPrivateData(keep);
        }
        target.applyPrivateData(entry.dataPrivate);
    }

    /** the server-owned timestamps of entry - none without an entry, like a vote that was never saved */
    private adoptTimestamps(target: VoteClass, entry: VoteClass | undefined): void {
        target.createdAt = entry?.createdAt;
        target.updatedAt = entry?.updatedAt;
    }

    /**
     * A remote option makes the form one choice too short (VotesService pads the list itself).
     * The baseline grows along: an appended empty choice is no change of the voter's, and a
     * dirty form would stop taking over the server state.
     */
    private padChoiceLists(): void {
        this.userVote?.initChoiceList(this.numberOfOptions);
        this._userVoteSinceLastSave?.initChoiceList(this.numberOfOptions);
    }

    public async deleteUserVote(ownerId: string, voteId: string, userId: string): Promise<void> {
        this.logger.log('deleteUserVote', ownerId, voteId, userId);
        // one atomic update: the contact part in votePrivate must not outlive the vote;
        // the realtime echo removes the vote from the list
        await this.afd.database.ref().update({
            [this.pathService.getUserVotePath(userId, ownerId, voteId)]: null,
            [this.pathService.getUserVotePrivatePath(userId, ownerId, voteId)]: null,
        });
    }

    get userVoteChoiceInvitationIsNull(): boolean {
        if (this.userVote.choiceInvitation === undefined ||
            this.userVote.choiceInvitation === null ||
            this.userVote.choiceInvitation === 0
        ) {
            return true
        }
        return false
    }

    canDeactivate() {
        return !this.hasUnsavedChanges || this.isLoggingOut
    }

    get hasUnsavedChanges(): boolean {
        return !isEqual(this.comparable(this.userVote), this.comparable(this._userVoteSinceLastSave))
    }

    /**
     * The persisted payload without the server-owned timestamps - they arrive in two echoes (local
     * estimate, then server value) and must never make the form dirty. 'public' leaves the contact
     * fields out as well: most list entries do not carry them.
     */
    private comparable(vote: VoteClass | undefined, part: 'all' | 'public' = 'all'): VoteData | undefined {
        if (!vote) return undefined;
        const data = part === 'public' ? vote.dataPublic : vote.data;
        delete data.createdAt;
        delete data.updatedAt;
        return data;
    }

    public async saveVote() {
        this.logger.log('saveVote', this.userVote)
        const length = this.optionsService.optionList.lengthIncludingDeleted;
        this.userVote.initChoiceList(length)
        this._isSaving = true;
        try {
            await this.votesService.persistVote(this.userVote) // no add - the realtime echo brings the vote into the list
        } finally {
            this._isSaving = false;
        }
        this.isNewVote = false;
        await this.pollParticipantsService.updateParticipants( // @todo should be better a backend function
            this.currentPollService.ownerId,
            this.headerService.headerClass.ownerName,
            this.currentPollService.pollId,
            this.headerService.headerClass.pollTitle,
            this.userVote.userName
        );
        if (
            this.authService.displayName === null ||
            this.authService.displayName === '' ||
            this.authService.isAnonymous
        ) {
            // side effect only, the vote is saved - nothing to show when it fails
            this.authService.setDisplayName(this.userVote.userName).catch(error => {
                if (isNetworkRequestFailed(error)) {
                    this.logger.warn('saveVote: setDisplayName failed, no connection', error?.code);
                } else {
                    this.logger.error('saveVote: setDisplayName failed', error);
                }
            });
        }
        await this.optionsService.saveOptionsCreatedByParticpant(this.userVote.userName);
        this.googleAnayltics.emitVote(this.headerService.headerClass.pollTemplate, this.headerService.headerClass.theme)
        this.resetVoteSinceLastSave()
    }

    generateUid(): string {
        return this.afd.createPushId();
    }
}
